/**
 * Mobile sticky add-to-cart bar.
 *
 * - Appears when the main "Add to cart" button scrolls above the viewport,
 *   hides again when the footer comes into view or the user scrolls back up.
 * - Mirrors the main button's label/disabled/loading state so sold-out and
 *   variant changes stay in sync.
 * - Submits the main product form (product-form.js handles the request and
 *   the cart drawer / notification), so no cart logic is duplicated here.
 */
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        // Never show the bar for products loaded inside the quick-add modal.
        if (this.closest('quick-add-modal, .quick-add-modal__content')) {
          this.remove();
          return;
        }

        // Move to <body> so ancestor transforms (animations, sticky columns)
        // can never break position: fixed.
        if (this.parentElement !== document.body) {
          document.body
            .querySelectorAll(`sticky-add-to-cart[data-section-id="${this.dataset.sectionId}"]`)
            .forEach((el) => el !== this && el.remove());
          document.body.appendChild(this);
          return;
        }

        this.form = document.getElementById(this.dataset.formId);
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
        this.button = this.querySelector('.sticky-atc__button');
        this.priceContainer = this.querySelector('.sticky-atc__price');

        if (!this.form || !this.mainButton || !this.button) return;

        this.buttonText = this.button.querySelector('span');
        this.mainButtonText = this.mainButton.querySelector('span');
        this.footer = document.querySelector('.footer, footer');
        this.mainVisible = true;
        this.footerVisible = false;

        if (document.querySelector('cart-drawer')) this.button.setAttribute('aria-haspopup', 'dialog');

        this.onClick = this.onClick.bind(this);
        this.button.addEventListener('click', this.onClick);

        if ('IntersectionObserver' in window) {
          this.mainObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                // Only show when the button is above the viewport (user scrolled past it).
                this.mainVisible = entry.isIntersecting || entry.boundingClientRect.top > 0;
                this.update();
              });
            },
            { threshold: 0 }
          );
          this.mainObserver.observe(this.mainButton);

          if (this.footer) {
            this.footerObserver = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  this.footerVisible = entry.isIntersecting;
                  this.update();
                });
              },
              { threshold: 0 }
            );
            this.footerObserver.observe(this.footer);
          }
        }

        this.stateObserver = new MutationObserver(() => this.syncState());
        this.stateObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });
        this.syncState();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (!event?.data || event.data.sectionId !== this.dataset.sectionId) return;
            const source = event.data.html?.getElementById(`price-${this.dataset.sectionId}`);
            const price = source?.querySelector('.price');
            if (price && this.priceContainer) {
              const compact = price.cloneNode(true);
              compact.classList.remove('price--large', 'price--show-badge');
              compact.querySelectorAll('.price__badge-sale, .price__badge-sold-out').forEach((el) => el.remove());
              this.priceContainer.replaceChildren(compact);
            }
          });
        }

        this.onSectionUnload = (event) => {
          if (event.detail?.sectionId === this.dataset.sectionId) this.remove();
        };
        document.addEventListener('shopify:section:unload', this.onSectionUnload);
      }

      disconnectedCallback() {
        this.mainObserver?.disconnect();
        this.footerObserver?.disconnect();
        this.stateObserver?.disconnect();
        this.unsubscribeVariantChange?.();
        this.button?.removeEventListener('click', this.onClick);
        document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      }

      update() {
        const show = !this.mainVisible && !this.footerVisible;
        this.classList.toggle('sticky-atc--visible', show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
      }

      syncState() {
        const disabled =
          this.mainButton.hasAttribute('disabled') || this.mainButton.getAttribute('aria-disabled') === 'true';
        this.button.toggleAttribute('disabled', disabled);
        this.button.classList.toggle('loading', this.mainButton.classList.contains('loading'));
        this.button.querySelector('.loading__spinner')?.classList.toggle(
          'hidden',
          !this.mainButton.classList.contains('loading')
        );
        if (this.mainButtonText && this.buttonText) {
          this.buttonText.textContent = this.mainButtonText.textContent;
        }
      }

      onClick(event) {
        event.preventDefault();
        if (this.button.hasAttribute('disabled')) return;
        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit();
        } else {
          this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      }
    }
  );
}
